/**
 * Guardrail Service
 * Handles guardrail-related API calls
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import { GuardrailAdapter, GuardrailFallbackAdapter } from '@/adapters';
import type {
  ApiResponse,
  Guardrail,
  GuardrailFallback,
  GuardrailStats,
  GuardrailFallbackStats,
  CreateGuardrailDto,
  UpdateGuardrailDto,
  CreateGuardrailFallbackDto,
  GuardrailsListResponseDto,
  GuardrailFallbacksListResponseDto,
  GuardrailStatsResponseDto,
  GuardrailFallbackStatsResponseDto,
  GuardrailResponseDto,
  GuardrailFallbackResponseDto
} from '@/types';

export const guardrailService = {
  /**
   * Get all guardrails
   */
  async getGuardrails(type?: 'system' | 'custom', status?: boolean, scope?: { bu_id?: string }): Promise<Guardrail[]> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (status !== undefined) params.append('status', status.toString());
    if (scope?.bu_id) params.append('bu_id', scope.bu_id);

    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.GUARDRAILS.BASE}?${queryString}` : API_ENDPOINTS.GUARDRAILS.BASE;

    const response = await api.get<ApiResponse<GuardrailsListResponseDto>>(url);
    return GuardrailAdapter.fromApiList(response.data.data.guardrails);
  },

  /**
   * Get guardrail by ID
   */
  async getGuardrailById(id: string): Promise<Guardrail> {
    const response = await api.get<ApiResponse<GuardrailResponseDto>>(API_ENDPOINTS.GUARDRAILS.BY_ID(id));
    return GuardrailAdapter.fromApi(response.data.data);
  },

  /**
   * Get system guardrails
   */
  async getSystemGuardrails(params?: { bu_id?: string }): Promise<Guardrail[]> {
    const response = await api.get<ApiResponse<GuardrailsListResponseDto>>(API_ENDPOINTS.GUARDRAILS.SYSTEM, { params });
    return GuardrailAdapter.fromApiList(response.data.data.guardrails);
  },

  /**
   * Get custom guardrails
   */
  async getCustomGuardrails(params?: { bu_id?: string }): Promise<Guardrail[]> {
    const response = await api.get<ApiResponse<GuardrailsListResponseDto>>(API_ENDPOINTS.GUARDRAILS.CUSTOM, { params });
    return GuardrailAdapter.fromApiList(response.data.data.guardrails);
  },

  /**
   * Get active guardrails
   */
  async getActiveGuardrails(params?: { bu_id?: string }): Promise<Guardrail[]> {
    const response = await api.get<ApiResponse<GuardrailsListResponseDto>>(API_ENDPOINTS.GUARDRAILS.ACTIVE, { params });
    return GuardrailAdapter.fromApiList(response.data.data.guardrails);
  },

  /**
   * Create a new guardrail
   */
  async createGuardrail(data: CreateGuardrailDto, scope: { bu_id?: string } = {}): Promise<Guardrail> {
    const payload = GuardrailAdapter.toCreateRequest(data);
    const response = await api.post<ApiResponse<GuardrailResponseDto>>(
      withBuScope(API_ENDPOINTS.GUARDRAILS.BASE, scope),
      payload
    );
    return GuardrailAdapter.fromApi(response.data.data);
  },

  /**
   * Update a guardrail
   */
  async updateGuardrail(id: string, data: UpdateGuardrailDto): Promise<Guardrail> {
    const payload = GuardrailAdapter.toUpdateRequest(data);
    const response = await api.put<ApiResponse<GuardrailResponseDto>>(API_ENDPOINTS.GUARDRAILS.BY_ID(id), payload);
    return GuardrailAdapter.fromApi(response.data.data);
  },

  /**
   * Delete a guardrail
   */
  async deleteGuardrail(id: string): Promise<void> {
    await api.delete<ApiResponse<void>>(API_ENDPOINTS.GUARDRAILS.BY_ID(id));
  },

  /**
   * Get guardrail statistics
   */
  async getGuardrailStats(params?: { bu_id?: string }): Promise<GuardrailStats> {
    const response = await api.get<ApiResponse<GuardrailStatsResponseDto>>(API_ENDPOINTS.GUARDRAILS.STATS, { params });
    return GuardrailAdapter.fromStatsApi(response.data.data);
  }
};

export const guardrailFallbackService = {
  /**
   * Get all guardrail fallback entries
   */
  async getGuardrailFallbacks(createdBy?: string, scope: { bu_id?: string } = {}): Promise<GuardrailFallback[]> {
    const params = new URLSearchParams();
    if (createdBy) params.append('createdBy', createdBy);
    if (scope.bu_id) params.append('bu_id', scope.bu_id);

    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.GUARDRAILS_FALLBACK.BASE}?${queryString}` : API_ENDPOINTS.GUARDRAILS_FALLBACK.BASE;

    const response = await api.get<ApiResponse<GuardrailFallbacksListResponseDto>>(url);
    return GuardrailFallbackAdapter.fromApiList(response.data.data.fallbacks);
  },

  /**
   * Get guardrail fallback by ID
   */
  async getGuardrailFallbackById(id: string): Promise<GuardrailFallback> {
    const response = await api.get<ApiResponse<GuardrailFallbackResponseDto>>(API_ENDPOINTS.GUARDRAILS_FALLBACK.BY_ID(id));
    return GuardrailFallbackAdapter.fromApi(response.data.data);
  },

  /**
   * Create a new guardrail fallback entry
   */
  async createGuardrailFallback(
    data: CreateGuardrailFallbackDto,
    scope: { bu_id?: string } = {}
  ): Promise<GuardrailFallback> {
    const payload = GuardrailFallbackAdapter.toCreateRequest(data);
    const response = await api.post<ApiResponse<GuardrailFallbackResponseDto>>(
      withBuScope(API_ENDPOINTS.GUARDRAILS_FALLBACK.BASE, scope),
      payload
    );
    return GuardrailFallbackAdapter.fromApi(response.data.data);
  },

  /**
   * Delete a guardrail fallback entry
   */
  async deleteGuardrailFallback(id: string): Promise<void> {
    await api.delete<ApiResponse<void>>(API_ENDPOINTS.GUARDRAILS_FALLBACK.BY_ID(id));
  },

  /**
   * Get guardrail fallback statistics
   */
  async getGuardrailFallbackStats(params?: { bu_id?: string }): Promise<GuardrailFallbackStats> {
    const response = await api.get<ApiResponse<GuardrailFallbackStatsResponseDto>>(
      API_ENDPOINTS.GUARDRAILS_FALLBACK.STATS,
      { params }
    );
    return GuardrailFallbackAdapter.fromStatsApi(response.data.data);
  }
};
